import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import gsap from 'gsap';
import { ErrorState } from '../components/ui';
import { getLocalizedField } from '../lib/format';
import { useReveal } from '../hooks/useReveal';

// Animated Section Component
function AnimatedSection({ item, getLocalizedContent, index }) {
  const sectionRef = useReveal({ index, y: 50, scale: 1, stagger: 0.1 });

  return (
    <section ref={sectionRef} className="prose prose-lg max-w-none">
      {item.image_url && (
        <img
          src={item.image_url}
          alt={getLocalizedContent(item, 'title')}
          className="w-full h-64 object-cover rounded-xl mb-6"
        />
      )}
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">
        {getLocalizedContent(item, 'title')}
      </h2>
      <div
        className="text-gray-600 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: getLocalizedContent(item, 'content') || '' }}
      />
    </section>
  );
}

export default function Brand() {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = useRef(null);

  const lang = i18n.language?.split('-')[0] || 'id';

  useEffect(() => {
    fetchContent();
  }, []);

  // Animate header on mount
  useEffect(() => {
    if (!loading && headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
      );
    }
  }, [loading]);

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('website_brand_content')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (fetchError) {
      console.error('Error fetching brand content:', fetchError);
      setError(fetchError);
    } else if (data) {
      setContent(data);
    }
    setLoading(false);
  };

  const getLocalizedContent = (item, field) => getLocalizedField(item, field, lang);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div ref={headerRef} className="text-center mb-12">
          <h1 className="section-title">{t('brand.title')}</h1>
          <p className="section-subtitle">{t('brand.subtitle')}</p>
        </div>

        {/* Content Sections */}
        {error ? (
          <ErrorState onRetry={fetchContent} />
        ) : (
          <div className="space-y-12">
            {content.map((item, index) => (
              <AnimatedSection
                key={item.id}
                item={item}
                getLocalizedContent={getLocalizedContent}
                index={index}
              />
            ))}

            {content.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">{t('brand.comingSoon', 'Content coming soon...')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
